import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { GlassCard, LoadingOverlay, useAlert } from '../../components';
import { getTransactions } from '../../services/firestoreService';
import { TRANSACTION_TYPES } from '../../config/firebase';
import { formatCurrency, formatDateTime } from '../../utils/helpers';
import './TransactionsScreen.css';

export const TransactionsScreen = () => {
    const navigate = useNavigate();
    const alert = useAlert();
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');

    const filters = [
        { key: 'all', label: 'All' },
        { key: TRANSACTION_TYPES.RECHARGE, label: 'Recharge' },
        { key: TRANSACTION_TYPES.WITHDRAW, label: 'Withdraw' },
        { key: TRANSACTION_TYPES.PURCHASE, label: 'Purchase' },
        { key: TRANSACTION_TYPES.DAILY_INCOME, label: 'Income' },
        { key: TRANSACTION_TYPES.GIFT_CODE, label: 'Gift' },
    ];

    useEffect(() => {
        loadTransactions();
    }, []);

    const loadTransactions = async () => {
        try {
            const data = await getTransactions();
            setTransactions(data);
        } catch (error) {
            alert.showError('Error', 'Failed to load transactions');
        } finally {
            setLoading(false);
        }
    };

    const getTransactionIcon = (type) => {
        switch (type) {
            case TRANSACTION_TYPES.RECHARGE:
            case TRANSACTION_TYPES.DAILY_INCOME:
            case TRANSACTION_TYPES.REFERRAL_BONUS:
            case TRANSACTION_TYPES.GIFT_CODE:
                return 'arrow_downward';
            case TRANSACTION_TYPES.WITHDRAW:
            case TRANSACTION_TYPES.PURCHASE:
                return 'arrow_upward';
            default:
                return 'swap_vert';
        }
    };

    const getTransactionColor = (type, status = 'success') => {
        if (status === 'rejected' || status === 'failed') return '#ef4444'; // Red
        if (status === 'pending') return '#f59e0b'; // Orange

        switch (type) {
            case TRANSACTION_TYPES.RECHARGE:
            case TRANSACTION_TYPES.DAILY_INCOME:
            case TRANSACTION_TYPES.REFERRAL_BONUS:
            case TRANSACTION_TYPES.GIFT_CODE:
                return '#3b82f6'; // Blue
            case TRANSACTION_TYPES.WITHDRAW:
            case TRANSACTION_TYPES.PURCHASE:
                return '#ef4444'; // Red for deductions
            default:
                return '#64748B';
        }
    };

    const getStatusText = (transaction) => {
        if (transaction.status) {
            if (transaction.status === 'approved' || transaction.status === 'completed') return 'COMPLETED';
            if (transaction.status === 'rejected') return 'FAILED';
            if (transaction.status === 'pending') return 'UNDER REVIEW';
            return transaction.status.toUpperCase();
        }
        return 'COMPLETED';
    };

    const getStatusColor = (transaction) => {
        const status = transaction.status || 'approved';
        if (status === 'pending') return '#f59e0b'; // Orange
        if (status === 'rejected' || status === 'failed') return '#ef4444'; // Red
        return '#10b981'; // Green for success
    };

    const filteredTransactions = filter === 'all'
        ? transactions
        : transactions.filter(t => t.type === filter);

    if (loading) {
        return <LoadingOverlay visible message="Loading transactions..." />;
    }

    return (
        <div className="transactions-screen">
            <div className="screen-header">
                <button className="back-btn" onClick={() => navigate(-1)}>
                    <span className="material-icons">arrow_back</span>
                </button>
                <h1 className="screen-title">Transactions</h1>
                <div style={{ width: 40 }}></div>
            </div>

            {/* Filters */}
            <div className="filters-container">
                {filters.map((f) => (
                    <button
                        key={f.key}
                        className={`filter-btn ${filter === f.key ? 'active' : ''}`}
                        onClick={() => setFilter(f.key)}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            {/* Transactions List */}
            <div className="transactions-list">
                {filteredTransactions.length > 0 ? (
                    filteredTransactions.map((transaction) => {
                        const isIncome = [
                            TRANSACTION_TYPES.RECHARGE,
                            TRANSACTION_TYPES.DAILY_INCOME,
                            TRANSACTION_TYPES.REFERRAL_BONUS,
                            TRANSACTION_TYPES.GIFT_CODE
                        ].includes(transaction.type);

                        const mainColor = getTransactionColor(transaction.type);
                        const statusColor = getStatusColor(transaction);

                        return (
                            <GlassCard key={transaction.id} className="transaction-card">
                                <div
                                    className="transaction-icon"
                                    style={{
                                        backgroundColor: `${mainColor}20`,
                                        color: mainColor
                                    }}
                                >
                                    <span className="material-icons">
                                        {getTransactionIcon(transaction.type)}
                                    </span>
                                </div>

                                <div className="transaction-info">
                                    <div className="transaction-type">
                                        {transaction.type?.replace(/_/g, ' ').toUpperCase()}
                                        <div className="transaction-id-tag">
                                            #{transaction.id.substring(0, 6).toUpperCase()}
                                        </div>
                                    </div>
                                    <span className="transaction-date">
                                        {formatDateTime(transaction.createdAt)}
                                    </span>
                                </div>

                                <div className="transaction-right">
                                    <span
                                        className="transaction-amount"
                                        style={{ color: mainColor }}
                                    >
                                        {isIncome ? '+' : ''}{formatCurrency(transaction.amount)}
                                    </span>
                                    <span
                                        className="status-text"
                                        style={{ color: statusColor }}
                                    >
                                        {getStatusText(transaction)}
                                    </span>
                                </div>
                            </GlassCard>
                        );
                    })
                ) : (
                    <div className="empty-state">
                        <span className="material-icons empty-icon">receipt_long</span>
                        <h3>No Transactions</h3>
                        <p>Your transaction history will appear here</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TransactionsScreen;
